// ============================================================================
// Operator Dashboard
// Feature: 136-dashboard-alignment (T021-T025)
// Created: 2026-01-11
// Updated: 2026-02-17 - Replaced mock data with live API calls (Constitution XXII)
// Description: Active alerts and operational monitoring dashboard for operators
// ============================================================================

// Updated: 2026-05-31 — Spec 160 T021: 8 KPI panels (same as Admin, no cost estimate)
import React, { Suspense, lazy } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Activity } from 'lucide-react';
import { CuFleetPanel } from '../../components/Dashboard/panels/CuFleetPanel';
import { CollectionHealthPanel } from '../../components/Dashboard/panels/CollectionHealthPanel';
import { CollectionCoveragePanel } from '../../components/Dashboard/panels/CollectionCoveragePanel';
import { ComputeFootprintPanel } from '../../components/Dashboard/panels/ComputeFootprintPanel';
import { StoragePanel } from '../../components/Dashboard/panels/StoragePanel';
import { GovernancePanel } from '../../components/Dashboard/panels/GovernancePanel';
import { EntityGrowthPanel } from '../../components/Dashboard/panels/EntityGrowthPanel';
import { apiClient } from '../../services/apiClient';
import type { DashboardKpis } from '../../../../shared/types/dashboard';
import { DualScrollTable, usePagination, PaginationControls } from '../../components/common/TableControls';
import { Extended } from '../../components/layout/Responsive';

import { PageHelpPanel } from '../../components/ui/PageHelpPanel';
import { PageLayout, DashboardPanel, StatCard, ErrorState } from '../../components/ui';
import { useScreenRefresh } from '../../hooks/useScreenRefresh';
import { lastCollectedAt } from '../../utils/lastCollectedAt';
import { operatorDashboardHelp } from '../../data/help/operatorDashboardHelp';
import { WidgetSkeleton } from '../../components/Dashboard/widgets/WidgetSkeleton';
import { BackupActivityPanel } from '../../modules/backup/components/dashboard/BackupActivityPanel';
import { AccessGate } from '../../components/access/AccessGate';

const ActiveAlertsWidget = lazy(() => import('../../components/Dashboard/ActiveAlertsWidget'));

const OperatorDashboard: React.FC = () => {
  // All operator data comes from the KPI endpoint — no separate queries needed
  const kpisQuery = useQuery<DashboardKpis>({
    queryKey: ['dashboard', 'kpis', 'Operator'],
    queryFn: async () => (await apiClient.get<DashboardKpis>('/api/dashboards/kpis')).data,
    staleTime: 30_000, refetchInterval: 30_000, refetchOnWindowFocus: false,
  });
  const kpis = kpisQuery.data;
  // Spec 226: the panels poll for themselves (owner decision 10); the header Refresh re-fetches them all.
  const pageRefresh = useScreenRefresh();
  // A failed load shows an error with a retry in place of the panels (below), never zeros and never a skeleton that
  // waits for good. A failed poll after that keeps the figures and shows the Refresh badge (FR-011).
  const kpisLoading = kpisQuery.isLoading;
  // Spec 298 FR-005: the whole request failed, and the page's one error (with its retry) already says so
  const kpisRequestFailed = kpisQuery.isError && !kpis;
  const notAvailable = <p className="py-6 text-center text-sm text-[var(--color-text-secondary)]">Not available: the dashboard figures did not load</p>;
  const ops = kpis?.operatorStats;
  const perf = kpis?.collectionPerf;

  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } = usePagination(ops?.capacities ?? []);

  return (
    <PageLayout
      title="Operations Dashboard"
      description="Real-time monitoring and job management"
      icon={<Activity className="h-6 w-6" />}
      actions={<PageHelpPanel content={operatorDashboardHelp} buttonSize="sm" />}
      onRefresh={pageRefresh.refresh}
      isRefreshing={pageRefresh.isRefreshing}
      lastFetchedAt={kpisQuery.dataUpdatedAt}
      collectedAt={lastCollectedAt(kpis?.collectionHealth)}
      refreshError={pageRefresh.error ?? kpisQuery.error}
    >
      {/* Spec 160 T021: KPI panels — same as Admin minus cost estimate */}
      {kpisQuery.isError && !kpis ? (
        <ErrorState
          className="mb-[var(--space-section)]"
          title="Dashboard figures could not be loaded"
          message="The capacity, storage, governance and growth figures did not arrive."
          onRetry={() => void kpisQuery.refetch()}
        />
      ) : (
      <div className="mb-[var(--space-section)]">
        {/* Row 1: CU Fleet + Collection Health */}
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CuFleetPanel data={kpis?.cuFleet} loading={kpisLoading} />
          <CollectionHealthPanel data={kpis?.collectionHealth} loading={kpisLoading} />
        </div>
        {/* Row 2: Storage + Governance */}
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <StoragePanel data={kpis?.storage} loading={kpisLoading} showCostEstimate={false} />
          <GovernancePanel data={kpis?.governance} loading={kpisLoading} />
        </div>
        {/* Row 3: Entity sparklines */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <EntityGrowthPanel label="Workspaces" data={kpis?.entities?.workspaces} loading={kpisLoading} />
          <EntityGrowthPanel label="Lakehouses" data={kpis?.entities?.lakehouses} loading={kpisLoading} color="#0ea5e9" />
          <EntityGrowthPanel label="Warehouses" data={kpis?.entities?.warehouses} loading={kpisLoading} color="#f59e0b" />
          <EntityGrowthPanel label="Container Apps" data={kpis?.entities?.containerApps} loading={kpisLoading} color="#10b981" />
        </div>
        {/* Row 4: Collection coverage scorecard (Spec 187) — self-fetching.
            Spec 197 US4: extended scorecard hidden on mobile (quick-glance only). */}
        <Extended>
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <CollectionCoveragePanel />
            <AccessGate area="dashboard.backup-tiles"><ComputeFootprintPanel /></AccessGate>
          </div>
        </Extended>
      </div>
      )}

      {/* Collection Job Quick Stats — real data from data_collection_runs */}
      <div className="mb-[var(--space-section)] grid grid-cols-2 gap-4 md:grid-cols-6">
        {[
          { label: 'Runs (24h)',    value: ops?.runs24h ?? '—',                       color: undefined },
          { label: 'Completed',     value: ops?.completed24h ?? '—',                  color: 'text-[var(--color-status-success)]' },
          { label: 'Failed (24h)',  value: ops?.failed24h ?? '—',                     color: (ops?.failed24h ?? 0) > 0 ? 'text-[var(--color-status-danger)]' : 'text-[var(--color-status-success)]' },
          { label: 'Running Now',   value: ops?.runningNow ?? '—',                    color: (ops?.runningNow ?? 0) > 0 ? 'text-[var(--color-status-info)]' : undefined },
          { label: 'Avg Duration',  value: perf?.avgDurationSeconds != null ? (perf.avgDurationSeconds >= 60 ? `${Math.round(perf.avgDurationSeconds/60)}m` : `${perf.avgDurationSeconds}s`) : '—', color: undefined },
          { label: 'Success Rate',  value: perf?.successRate7d != null ? `${perf.successRate7d}%` : '—', color: (perf?.successRate7d ?? 100) >= 95 ? 'text-[var(--color-status-success)]' : (perf?.successRate7d ?? 100) >= 80 ? 'text-[var(--color-status-warning)]' : 'text-[var(--color-status-danger)]' },
          // Spec 193 FR-004: open Alert Health across the unified feed (all resource types).
          { label: 'Alert Health',  value: kpis?.alertHealth?.totalOpen ?? '—', color: (kpis?.alertHealth?.openCritical ?? 0) > 0 ? 'text-[var(--color-status-danger)]' : (kpis?.alertHealth?.totalOpen ?? 0) > 0 ? 'text-[var(--color-status-warning)]' : 'text-[var(--color-status-success)]' },
        ].map(({ label, value, color }) =>
          kpisLoading ? (
            <div key={label} className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 shadow-[var(--shadow-md)]">
              <div className="text-sm font-medium text-[var(--color-text-secondary)]">{label}</div>
              <div className="mt-1 h-8 w-1/2 animate-pulse rounded bg-[var(--color-bg-subtle)]" />
            </div>
          ) : (
            <StatCard key={label} label={label} value={value} valueClassName={color} />
          )
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Alerts - Priority — T024: Lazy loaded */}
        <div className="lg:col-span-1">
          <Suspense fallback={<WidgetSkeleton rows={4} title="Active Alerts" />}>
            <ActiveAlertsWidget maxItems={8} showAcknowledge />
          </Suspense>
        </div>

        {/* Running Collection Tiers */}
        <div className="lg:col-span-2">
          <DashboardPanel
            title="Running Collection Tiers"
            actions={<Link to="/operations/collection-jobs" className="text-sm text-[var(--color-accent)] hover:underline">View All Jobs →</Link>}
          >
            {kpisLoading ? (
              <WidgetSkeleton rows={3} />
            ) : !ops ? (
              // Spec 298 FR-005: the section was not read, so nothing can be said about what is running
              kpisRequestFailed ? notAvailable : <ErrorState title="Could not read the running collection tiers" />
            ) : !ops.runningTiers.length ? (
              <div className="py-8 text-center text-[var(--color-text-secondary)]">
                <svg className="mx-auto mb-2 h-10 w-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm">No collection tiers running</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ops.runningTiers.map((tier) => {
                  const elapsedMin = Math.floor((Date.now() - new Date(tier.startedAt).getTime()) / 60000);
                  return (
                    <div key={tier.tierName + tier.startedAt} className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-blue-900 dark:text-blue-200">{tier.tierName} collection</span>
                        <span className="text-xs text-blue-600 dark:text-blue-400">{elapsedMin}m running</span>
                      </div>
                      <div className="mt-2 h-1.5 w-full rounded-full bg-blue-200 dark:bg-blue-800">
                        <div className="h-1.5 animate-pulse rounded-full bg-blue-600" style={{ width: '60%' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DashboardPanel>
        </div>

        {/* Capacity Status — real capacities with CU metrics.
            Spec 197 US4: extended capacity table hidden on mobile (quick-glance only). */}
        <Extended>
        <div className="lg:col-span-3">
          <DashboardPanel
            title="Capacity Status"
            actions={<Link to="/capacity/list" className="text-sm text-[var(--color-accent)] hover:underline">View All Capacities →</Link>}
          >
            {kpisLoading ? (
              <WidgetSkeleton rows={4} />
            ) : !ops ? (
              kpisRequestFailed ? notAvailable : <ErrorState title="Could not read the capacities" />
            ) : !ops.capacities.length ? (
              <p className="py-6 text-center text-sm text-[var(--color-text-secondary)]">No active capacities</p>
            ) : (
              <>
                <DualScrollTable>
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-[var(--color-border)] bg-[var(--color-bg-subtle)] text-left text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                      {['Capacity','Type','SKU','CU %','Status','Last Metric',''].map(h => (
                        <th key={h} className="px-2 py-1.5">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((c) => {
                      // An unmeasured capacity (Spec 286 T160 item 5) is "no data" in the secondary color, never 0%.
                      const cuColor = c.cuUsedPct === null ? 'text-[var(--color-text-secondary)]' : c.cuUsedPct >= 90 ? 'text-[var(--color-status-danger)]' : c.cuUsedPct >= 80 ? 'text-[var(--color-status-warning)]' : 'text-[var(--color-status-success)]';
                      const ago = c.lastMetricAt
                        ? (() => { const m = Math.floor((Date.now() - new Date(c.lastMetricAt).getTime()) / 60000); return m < 60 ? `${m}m ago` : `${Math.floor(m/60)}h ago`; })()
                        : 'No data';
                      return (
                        <tr key={c.id} className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-bg-subtle)]">
                          <td className="px-2 py-1.5 text-sm font-medium text-[var(--color-text-primary)]">{c.name}</td>
                          <td className="px-2 py-1.5 text-xs text-[var(--color-text-secondary)]">{c.type}</td>
                          <td className="px-2 py-1.5 text-xs text-[var(--color-text-secondary)]">{c.sku ?? '—'}</td>
                          <td className="px-2 py-1.5">
                            <span className={`text-sm font-medium ${cuColor}`}>
                              {c.isPaused ? 'Paused' : c.cuUsedPct === null ? 'no data' : `${c.cuUsedPct.toFixed(1)}%`}
                            </span>
                          </td>
                          <td className="px-2 py-1.5">
                            <span className={`rounded px-1.5 py-0.5 text-xs ${c.status === 'Active' || c.status === 'Succeeded' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'}`}>
                              {c.status}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 text-xs text-[var(--color-text-secondary)]">{ago}</td>
                          <td className="px-2 py-1.5">
                            <Link to={`/capacity-metrics/${c.id}`} className="text-xs text-[var(--color-accent)] hover:underline">Metrics</Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </DualScrollTable>
                <PaginationControls page={page} pageSize={pageSize} total={total} totalPages={totalPages} start={start} shown={pageItems.length} onPage={setPage} onPageSize={changeSize} />
              </>
            )}
          </DashboardPanel>
        </div>
        </Extended>

        {/* System Health — heartbeat-powered (same as Admin) */}
        <div className="lg:col-span-3">
          <DashboardPanel
            title="System Health"
            actions={<Link to="/infrastructure/health" className="text-sm text-[var(--color-accent)] hover:underline">Details →</Link>}
          >
            {kpisLoading ? (
              <WidgetSkeleton rows={3} />
            ) : !kpis?.systemHealth ? (
              <p className="text-sm text-[var(--color-text-secondary)]">No heartbeat data — run the 5min tier</p>
            ) : (() => {
              const h = kpis.systemHealth!;
              const ageMs = Date.now() - new Date(h.checkTime).getTime();
              const ageStr = Math.floor(ageMs/60000) > 0 ? `${Math.floor(ageMs/60000)}m ${Math.floor((ageMs%60000)/1000)}s ago` : `${Math.floor(ageMs/1000)}s ago`;
              const services = [
                { label: h.dbName,      ok: h.dbReachable,     sub: h.dbLatencyMs != null ? `${h.dbLatencyMs}ms` : null },
                { label: h.kvName,      ok: h.kvReachable,     sub: null },
                { label: 'Fabric API',  ok: h.fabricReachable, sub: null },
              ];
              return (
                <div className="space-y-2">
                  <div className={`flex items-center justify-between rounded p-2 text-sm font-medium ${
                    h.overallStatus === 'HEALTHY' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                    h.overallStatus === 'DEGRADED' ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400' :
                    'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    <span>{h.overallStatus === 'HEALTHY' ? '✓' : h.overallStatus === 'DEGRADED' ? '⚠' : '✗'} {h.overallStatus}</span>
                    <span className="text-xs font-normal opacity-75">{ageStr}</span>
                  </div>
                  {services.map(({ label, ok, sub }) => (
                    <div key={label} className="flex items-center justify-between border-b border-[var(--color-border)] py-1 text-sm last:border-0">
                      <span className="max-w-[55%] truncate text-[var(--color-text-secondary)]" title={label}>{label}</span>
                      <span className={`flex items-center gap-1 font-medium ${ok === null ? 'text-[var(--color-text-secondary)]' : ok ? 'text-[var(--color-status-success)]' : 'text-[var(--color-status-danger)]'}`}>
                        {ok === null ? '— N/A' : ok ? '✓ Reachable' : '✗ Down'}
                        {sub && <span className="ml-1 text-xs font-normal text-[var(--color-text-secondary)]">({sub})</span>}
                      </span>
                    </div>
                  ))}
                  {h.errorDetails && <p className="mt-1 truncate text-xs text-[var(--color-status-danger)]">{h.errorDetails}</p>}
                </div>
              );
            })()}
          </DashboardPanel>
        </div>

        {/* Backup activity trend — same diagram as the Backup command center.
            Spec 285: backup-licensed tenants only. */}
        <AccessGate area="dashboard.backup-tiles">
          <Extended>
            <div className="lg:col-span-3">
              <BackupActivityPanel />
            </div>
          </Extended>
        </AccessGate>
      </div>
    </PageLayout>
  );
};

export default OperatorDashboard;
